/**
 * Operator layout preference: cap the page width, or use the whole viewport.
 *
 * The default caps at `max-w-6xl` because most of this app is forms, and a
 * text input stretched across a 2560px monitor is harder to use, not easier.
 * But the LIST pages are wide tables — the roster is nine columns, and column
 * labels plus long values (an email, a client name, a cohort name) do not
 * compress. On a wide screen the cap wastes exactly the space those tables
 * need, and the alternative is scrolling a table sideways while empty margin
 * sits either side of it.
 *
 * So it is a preference, not a breakpoint: the operator decides, and the
 * choice sticks. Persisted in `localStorage` — this is a per-person display
 * choice, not something that belongs on the server.
 *
 * The CONTROL is rendered by `ListPage`, beside the search box, so it appears
 * only on the pages it affects. It was briefly in the nav, which meant it
 * showed on every form page and did nothing there.
 *
 * Deliberately applies to LIST pages only. Detail pages and forms stay capped:
 * their content is prose and inputs, where a long line length is the problem
 * rather than the solution. The tables inside them are capped previews (≤10
 * rows, few columns) and do not have the problem this solves.
 *
 * Jotai rather than react-query — this is client state, which is exactly the
 * split ADR-0056 keeps: Jotai owns "what has this operator chosen",
 * TanStack Query will own "what does the server say".
 */
import { atomWithStorage } from 'jotai/utils'

/** True = use the full viewport width on list pages. */
export const fullWidthAtom = atomWithStorage('coach-admin-full-width', false)

/**
 * True = the filter rail is collapsed to its icon button. Same reasoning as
 * the width preference: filters are set once and left alone, so an operator
 * working a wide table can trade the rail for ~16rem of columns — and the
 * choice sticks, per person, across lists (one habit, not a per-page state).
 */
export const filtersCollapsedAtom = atomWithStorage(
  'coach-admin-filters-collapsed',
  false,
)

/**
 * True = the desktop Admin navigation is reduced to its icon rail.
 *
 * This is the same kind of per-operator layout choice as table width and the
 * filter rail. It is controlled through Jotai rather than shadcn's cookie
 * default because this Vite SPA has no server-rendered layout to hydrate.
 */
export const sidebarCollapsedAtom = atomWithStorage(
  'coach-admin-sidebar-collapsed',
  false,
)

/** The container class for a list page, given the preference. */
export function listContainerClass(fullWidth: boolean): string {
  return fullWidth ? 'max-w-none' : 'max-w-6xl mx-auto'
}
